import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Typography from '../../atoms/typography';

import { logAmplitude } from '../../utils/amplitude';

const FILTERED_EMO_PROPS = new Set(['active', 'contentHeight']);

const dynamicHeight = ({ contentHeight }) => `max-height: ${contentHeight}px`;

const AccordionTab = styled('dl', {
  shouldForwardProp: (prop) => !FILTERED_EMO_PROPS.has(prop)
})`
  overflow: hidden;
  transition: max-height ${(props) => props.theme.animation.fast} ${(props) => props.theme.animation.easeInQuad};

  ${({ active, contentHeight }) => {
    const defaultHeight = '35';
    return `max-height: ${active ? contentHeight() : defaultHeight}px;`;
  }}
  }

  ${dynamicHeight};

  ~ dl {
    border-top: 0;
  }
`;

const AccordionTitle = styled(Typography, {
  shouldForwardProp: (prop) => !FILTERED_EMO_PROPS.has(prop)
})`
  ${({ active, theme }) => (
    theme.filterArrow(active ? 'up' : 'down', theme.color.brand, 'right 1px center', 7)
  )}
  color: ${({ theme }) => theme.color.bluePrimary};
  cursor: pointer;
  display: flex;
  line-height: 3.5rem;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  margin-bottom: ${({ theme }) => theme.modularScale.sixteen};
`;

const AccordionSubTitle = styled(Typography)`
  color: ${(props) => props.theme.color.bluePrimary};
  flex: 1;
  display: flex;
  letter-spacing: initial;
  text-align: right;
  text-transform: initial;
  padding-right: 2rem;
`;

const AccordionContent = styled('dd')`
  padding-bottom: ${({ theme }) => theme.modularScale.sixteen};
`;

export const PropertyList = styled(Typography)`
  list-style-type: disc;
  margin-left: 1.75rem};
  margin-right: 1.75rem;

  b {
    font-weight: bold;
  }

  ul > li {
    list-style-type: disc;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    margin-left: 1.75rem;
    margin-right: 1.75rem;
  }
`;

class ProductDetailAccordion extends Component {
  constructor(props) {
    super(props);

    const descriptionOpen = !!this.props.isDescription;

    this.state = {
      active: descriptionOpen
    };

    this.accordionRef = createRef();

    this.toggleAccordion = this.toggleAccordion.bind(this);
    this.getHeight = this.getHeight.bind(this);
  }

  getHeight() {
    return this.accordionRef.scrollHeight ?? 0;
  }

  toggleAccordion() {
    if (!this.state.active && Object.keys(this.props.product).length) {
      logAmplitude('PDP Interaction', {
        product: this.props.product,
        interactionType: 'Opened Product Property Accordion',
        position: this.props.title
      });
    }

    this.setState((prevState) => ({ active: !prevState.active }));
  }

  render() {
    return (
      <AccordionTab
        data-vwo-id="MobilePaidAccordionTab"
        ref={(node) => { this.accordionRef = node; }}
        active={this.state.active ? 'true' : undefined}
        contentHeight={this.getHeight}
      >
        <AccordionTitle
          data-vwo-id="MobilePaidAccordionTitle"
          element="dt"
          like="label-6"
          active={this.state.active ? 'true' : undefined}
          onClick={this.toggleAccordion}
        >
          {this.props.title}
          {
            this.props.subtitle && (
              <AccordionSubTitle
                data-vwo-id="MobilePaidAccordionSubTitle"
                element="span"
                like="dec-1"
              >
                {this.props.subtitle}
              </AccordionSubTitle>
            )
          }

        </AccordionTitle>
        <AccordionContent
          data-vwo-id="MobilePaidAccordionContent"
          active={this.state.active ? 'true' : undefined}
        >
          {this.props.children}
        </AccordionContent>
      </AccordionTab>
    );
  }
}

ProductDetailAccordion.defaultProps = {
  subtitle: '',
  isDescription: false
};

ProductDetailAccordion.propTypes = {
  product: PropTypes.object.isRequired,
  children: PropTypes.any.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.string.isRequired,
  isDescription: PropTypes.bool
};

export default ProductDetailAccordion;
